import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { OverviewCard } from '@iot-platform/models/common';

import { ExampleRow } from '../../models/formula-example-row.model';
import { FormulaEditorConfig } from '../../models/formula-editor-config.model';
import { CUSTOM_FUNCTION_NAMES } from '../../utils/formula-custom-functions';
import { DEFAULT_FORMULA_EDITOR_CONFIG } from '../../utils/formula-editor-config';
import { OverviewCardComponent } from '../../../overview-card/overview-card.component';
import { FormulaExamplesTableComponent } from './formula-examples-table/formula-examples-table.component';

/**
 * Overlay panel showing a static reference guide for the formula editor.
 * Rendered via CDK ConnectedOverlay from FormulaQueryBuilderComponent.
 *
 * Layout uses OverviewCardComponent with three collapsible sections:
 * Operators, Conditions, and Functions — each delegated to FormulaExamplesTableComponent.
 */
@Component({
  selector: 'iot-platform-ui-formula-help-guide',
  standalone: true,
  imports: [OverviewCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<iot-platform-ui-overview-card [overviewCard]="card()" />`,
  styleUrls: ['./formula-help-guide.component.scss']
})
export class FormulaHelpGuideComponent {
  readonly config = input<FormulaEditorConfig>(DEFAULT_FORMULA_EDITOR_CONFIG);
  // ── Static example data ──────────────────────────────────────────────────
  readonly operatorsData: ExampleRow[] = [
    { expression: 'Power / Exponent', exampleText: '2^3', previewHTML: '2<sup class="text-xs">3</sup>', result: '= 8' },
    { expression: 'Greater than', exampleText: '15 > 10', previewHTML: '15 &gt; 10', result: '= true' },
    { expression: 'Greater than or equal', exampleText: '10 >= 10', previewHTML: '10 &ge; 10', result: '= true' },
    { expression: 'Less than', exampleText: '2 < 1', previewHTML: '2 &lt; 1', result: '= false' },
    { expression: 'Less than or equal', exampleText: '5 <= 12', previewHTML: '5 &le; 12', result: '= true' },
    { expression: 'Equality', exampleText: '2+3 == 5', previewHTML: '2 + 3 = 5', result: '= true' },
    { expression: 'Inequality', exampleText: '10 != 5', previewHTML: '10 &ne; 5', result: '= true' }
  ];

  readonly conditionData: ExampleRow[] = [
    {
      expression: 'IF (condition)<br>THEN (value A)<br>OTHERWISE (value B)',
      exampleText: 'Var1 > 30 ? 1 : 0',
      previewHTML:
        '<div class="flex items-start gap-1 font-serif">' +
        '<span class="text-3xl font-light leading-none mr-1">{</span>' +
        '<div class="flex flex-col gap-0.5 text-xs leading-relaxed">' +
        '<span><span class="font-sans text-blue-700 font-medium">1</span>&nbsp; if <em>Var1</em> &gt; 30</span>' +
        '<span><span class="font-sans text-blue-700 font-medium">0</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; otherwise</span>' +
        '</div>' +
        '</div>',
      result: '',
      layout: 'wide'
    }
  ];

  readonly functionData: ExampleRow[] = [
    { expression: 'Square root', exampleText: 'sqrt(25)', previewHTML: '&radic;<span class="border-t border-current pt-px">25</span>', result: '= 5' },
    { expression: 'Absolute value', exampleText: 'abs(-10)', previewHTML: '|&minus;10|', result: '= 10' },
    { expression: 'MAX value', exampleText: 'max(1, 10, 3)', previewHTML: '<strong>max</strong>(1, 10, 3)', result: '= 10' },
    { expression: 'MIN value', exampleText: 'min(1, 10, 3)', previewHTML: '<strong>min</strong>(1, 10, 3)', result: '= 1' },
    { expression: 'Average', exampleText: 'mean(2, 4, 6)', previewHTML: '<strong>mean</strong>(2, 4, 6)', result: '= 4' },
    { expression: 'Median value', exampleText: 'median(1, 5, 9)', previewHTML: '<strong>median</strong>(1, 5, 9)', result: '= 5' },
    { expression: 'Logical OR', exampleText: '(5 > 2) or (10 < 20)', previewHTML: '(5 &gt; 2) &or; (10 &lt; 20)', result: '= true' },
    { expression: 'Logical AND', exampleText: '(5 > 2) and (10 < 20)', previewHTML: '(5 &gt; 2) &and; (10 &lt; 20)', result: '= true' },
    { expression: 'Logical NOT', exampleText: 'not (5 > 10)', previewHTML: '&not;(5 &gt; 10)', result: '= true' }
  ];

  readonly customFunctionData: ExampleRow[] = [
    {
      expression: 'Min/Max scale',
      exampleText: 'minMaxFn(1, 0, 2, -1, 1)',
      previewHTML: '<strong>minMaxFn</strong>(x, minR, maxR, minD, maxD)',
      result: '= 0'
    },
    {
      expression: 'Slope + Shift',
      exampleText: 'slopeShiftFn(5, 2, -1)',
      previewHTML: 'x &times; slope + shift',
      result: '= 9'
    },
    {
      expression: 'Delta (spread)',
      exampleText: 'deltaFn(3, 10, 7)',
      previewHTML: '<strong>max</strong> &minus; <strong>min</strong>',
      result: '= 7'
    },
    {
      expression: 'Is zero',
      exampleText: 'isZero(0)',
      previewHTML: 'x = 0',
      result: '= true'
    }
  ];

  // ── OverviewCard definition ──────────────────────────────────────────────
  readonly card = computed<OverviewCard>(() => {
    const cfg = this.config();
    const hasCustomFunctions = !cfg.restrictFunctions || CUSTOM_FUNCTION_NAMES.some((fn) => cfg.allowedFunctions.includes(fn));

    const sections = [
      {
        title: 'I18N_FORMULA.HELP_GUIDE.OPERATORS_TITLE',
        collapsible: true,
        expanded: true,
        properties: [
          {
            width: '100%',
            componentRef: {
              ref: FormulaExamplesTableComponent,
              inputs: { rows: this.operatorsData }
            }
          }
        ]
      },
      {
        title: 'I18N_FORMULA.HELP_GUIDE.CONDITION_TITLE',
        collapsible: true,
        expanded: true,
        properties: [
          {
            width: '100%',
            componentRef: {
              ref: FormulaExamplesTableComponent,
              inputs: { rows: this.conditionData }
            }
          }
        ]
      },
      {
        title: 'I18N_FORMULA.HELP_GUIDE.FUNCTIONS_TITLE',
        collapsible: true,
        expanded: true,
        properties: [
          {
            width: '100%',
            componentRef: {
              ref: FormulaExamplesTableComponent,
              inputs: { rows: this.functionData }
            }
          }
        ]
      },
      {
        title: 'I18N_FORMULA.HELP_GUIDE.CUSTOM_FUNCTIONS_TITLE',
        collapsible: true,
        expanded: false,
        visibleExpression: () => !!hasCustomFunctions,
        properties: [
          {
            width: '100%',
            componentRef: {
              ref: FormulaExamplesTableComponent,
              inputs: { rows: this.customFunctionData }
            }
          }
        ]
      }
    ];

    return { sections };
  });
}

